import RewardedPopup from "@/components/RewardedPopup.vue";
import { SPECIAL_BUILDING_PACK_DURATION } from "@/config/constants";
import { useEventStore } from "@/event";
import ChestRepository from "@/game/ChestRepository";
//import { activeOfferConfig } from "@/game/config/offerConfig";
import type { OfferConfig } from "@/game/config/offerConfig";
import { getOfferConfig } from "@/game/config/offerConfig";
import type { FrameName } from "@/game/entities/config";
import { tilerepoPremiumGemsBuildings } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import { ChestType } from "@/game/entities/objects/chests";
import type TileEntity from "@/game/entities/TileEntity";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import { type Timestamp } from "@/types";
import type { ShopItem } from "@/types/shop";
import { EventCategory, OfferType, SkinTypes } from "@/types/ui";
import { ceilNumber, floorNumber } from "@/utils/number";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { translate } from "@/utils/translate";
import { defineStore } from "pinia";
import { markRaw, type ComputedRef } from "vue";
import useQuestStore from "./useQuestStore";
import useRuntimeStore, { SuperClickerEnum } from "./useRuntimeStore";
import useSettlementsStore from "./useSettlementsStore";
import useUserStore from "./useUserStore";

export enum RewardType {
	COINS = "coins",
	GEMS = "gems",
	CLICKER_2X = "clicker2x",
	CLICKER_3X = "clicker3x",
	CLICKER_5X = "clicker5x",
	CHESTS = "chests",
	SUPER_CLICKERS = "superClickers",
	EVENTIUM = "eventium",
}

export interface RewardOffer {
	type: RewardType;
	amount: number;
	icon: string;
	description: string;
}

interface EventAd {
	current: number;
	max: number;
	lastWatchedTimestamp: Date | null;
}

// New offer system types edit
type BaseInGameShopItem = Omit<ShopItem, "attributes">;
type NewAttributes = Omit<ShopItem["attributes"], "template" | "slots"> & {
	type?: OfferType;
	building_image?: string;
	decorIndex?: number;
	value?: Record<ResourceType | string, number>;
	bonus?: Record<ResourceType, number>;
	originValue?: Record<ResourceType | string, number>;
	rewards?:
		| {
				type?: string;
				value?: Record<string, number>;
		  }
		| Record<string, number>;
};

export interface InGameShopItem extends BaseInGameShopItem {
	attributes: NewAttributes;
	expired_at?: number;
}
// END: New offer system types edit

export interface OfferStoreState {
	isVisible: boolean;
	currentOffer: RewardOffer | null;
	isActive: boolean;
	lastOfferTime: number;
	gameStartTime: number;
	rewardCooldowns: Record<RewardType, number>;
	specialBuildingPack: InGameShopItem | null;
	lastBuyedSpecialBuildingFrameName: FrameName | null;
	eventAdLimit: EventAd;
}

type OfferStore = {
	$getPayload: () => OfferStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<OfferStoreState>) => void;
	isVisible: ComputedRef<boolean>;
	currentOffer: ComputedRef<RewardOffer | null>;
	gameStartTime: ComputedRef<number>;
	specialBuildingPack: ComputedRef<InGameShopItem | null>;
	lastBuyedSpecialBuildingFrameName: ComputedRef<FrameName | null>;
	eventAdLimit: ComputedRef<EventAd>;
	setVisibility: (visible: boolean) => void;
	generateOffer: () => void;
	acceptOffer: () => void;
	rejectOffer: () => void;
	checkAndShowOffer: (force?: boolean) => void;
	applyReward: () => boolean;
	canShowOffer(force: boolean): boolean;
	setActive: (active: boolean) => void;
	resetOffer: () => void;
	setSpecialBuildingPack: (force: boolean) => void;
	clearSpecialBuildingPack: () => void;
	setLastBuyedSpecialBuildingFrameName: (frameName: FrameName) => void;
	setEventAdWatched: () => void;
	resetEventAdWatched: () => void;
	resetEventState: () => void;
};

type OfferStoreDiffAction = {
	// type: "set";
	key: keyof OfferStoreState;
	currentValue: OfferStoreState[keyof OfferStoreState];
	value: OfferStoreState[keyof OfferStoreState];
	// reason?: string | false;
	timestamp: Timestamp;
};

type OfferStorePayload = {
	state: OfferStoreState;
	actions: Array<OfferStoreDiffAction>;
};

const STORE_NAME = "offerStore";

// Use configuration instead of hardcoded values
/*const REWARD_COOLDOWNS = activeOfferConfig.timing.rewardCooldowns;
const MIN_GAME_TIME_FOR_OFFER = activeOfferConfig.timing.minGameTimeForOffer;
const OFFER_COOLDOWN = activeOfferConfig.timing.offerCooldown;*/

export default defineStore(STORE_NAME, (): OfferStore => {
	const state = createImmutableState<OfferStoreState>({
		isVisible: false,
		currentOffer: null,
		isActive: false,
		lastOfferTime: 0,
		gameStartTime: Date.now(),
		specialBuildingPack: null,
		lastBuyedSpecialBuildingFrameName: null,
		rewardCooldowns: {
			[RewardType.COINS]: 0,
			[RewardType.GEMS]: 0,
			[RewardType.CLICKER_2X]: 0,
			[RewardType.CLICKER_3X]: 0,
			[RewardType.CLICKER_5X]: 0,
			[RewardType.CHESTS]: 0,
			[RewardType.SUPER_CLICKERS]: 0,
			[RewardType.EVENTIUM]: 0,
		},
		eventAdLimit: {
			current: 0,
			max: 5,
			lastWatchedTimestamp: null,
		},
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): OfferStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof OfferStoreState>;
		return keys.reduce(
			(
				acc: OfferStoreState,
				key: keyof OfferStoreState,
			): OfferStoreState => ({
				...acc,
				[key]: data[key],
			}),
			{} as OfferStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<OfferStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<OfferStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched: boolean = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: OfferStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): OfferStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	const getActiveConfig = (): OfferConfig => {
		const userStore = useUserStore();
		return getOfferConfig(userStore.abTestGroup);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<OfferStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const isVisible = getImmutableStateProperty(state, "isVisible", false);
	const currentOffer = getImmutableStateProperty(state, "currentOffer", null);
	const gameStartTime = getImmutableStateProperty(
		state,
		"gameStartTime",
		Date.now(),
	);

	const specialBuildingPack = getImmutableStateProperty(
		state,
		"specialBuildingPack",
		null,
	);

	const lastBuyedSpecialBuildingFrameName = getImmutableStateProperty(
		state,
		"lastBuyedSpecialBuildingFrameName",
		null,
	);
	const eventAdLimit = getImmutableStateProperty(state, "eventAdLimit", {
		current: 0,
		max: 5,
		lastWatchedTimestamp: null,
	});

	const setSpecialBuildingPack = (force: boolean = false): void => {
		if (!force) return;
		const userStore = useUserStore();

		const DISCOUNT = userStore.isPayingCustomer ? 15 : 25; // percentualna zlava platic/neplatic
		const MIN_GEMS_VALUE = 300;
		const MAX_GEMS_VALUE = 800;

		const tileRepo = useTileRepository();

		const premiumBuildingsEntities: Set<TileEntity> = new Set();

		// zoznam premiovych budov + vyfiltrovanie iba odomknutych
		tilerepoPremiumGemsBuildings.forEach((building: FrameName): void => {
			const entity = tileRepo.createEntityByFrameName(building);
			if (
				entity?.requirements &&
				entity.requirements.has(ResourceType.LEVEL) &&
				entity.requirements.get(ResourceType.LEVEL)! <=
					useSettlementsStore().mainLevel
			) {
				premiumBuildingsEntities.add(entity as TileEntity);
			}
		});

		let availableBuildings: TileEntity[] = Array.from(
			premiumBuildingsEntities,
		);

		if (
			availableBuildings.length > 1 &&
			lastBuyedSpecialBuildingFrameName
		) {
			availableBuildings = availableBuildings.filter(
				(building: TileEntity): boolean =>
					building.frameName !==
					lastBuyedSpecialBuildingFrameName.value,
			);
		}

		// random jedna budova
		const premiumBuildingEntity = availableBuildings[
			floorNumber(Math.random() * availableBuildings.length)
		] as TileEntity;

		if (!premiumBuildingEntity) return;
		const resourcesStore = useResourcesStore();

		//  ratame maximalne s 1500 gemami
		const userGems =
			resourcesStore.gems > MAX_GEMS_VALUE
				? MAX_GEMS_VALUE
				: resourcesStore.gems < MIN_GEMS_VALUE
					? MIN_GEMS_VALUE
					: resourcesStore.gems;

		// gemy hraca / suma budovy - zaokruhlit nahor
		const buildingCount = ceilNumber(
			userGems / premiumBuildingEntity.baseGemsPrice,
		);

		const basePackPrice =
			buildingCount * premiumBuildingEntity.baseGemsPrice;

		// vypocet kolko budov si moze kupit bez zlavy
		const packCount = ceilNumber(
			(basePackPrice * 100) /
				(100 - DISCOUNT) /
				premiumBuildingEntity.baseGemsPrice,
		);

		// vypocet kolko stoji pack bez zlavy
		const packPrice = packCount * premiumBuildingEntity.baseGemsPrice;

		// vypocet kolko stoji pack so zlavou
		const packPriceWithDiscount = floorNumber(
			packPrice * ((100 - DISCOUNT) / 100),
		);

		const startDate = new Date();
		const endDate = new Date(
			startDate.getTime() + SPECIAL_BUILDING_PACK_DURATION,
		);

		const packName = `lok_${premiumBuildingEntity.frameName.replace(".png", "").toLowerCase()}_pack`;

		const pack = {
			store_id: packName,
			is_active: true,
			name: packName,
			start_date: startDate,
			end_date: endDate,
			show_in_left_box: true,
			attributes: {
				type: OfferType.SPECIAL_BUILDING,
				originValue: {
					[ResourceType.GEMS]: packPrice,
				} as Record<ResourceType, number>,
				value: {
					[ResourceType.GEMS]: packPriceWithDiscount,
				} as Record<ResourceType, number>,
				rewards: {
					[premiumBuildingEntity.frameName]: packCount,
				},
				percentage_discount: DISCOUNT as number,
				building_image: premiumBuildingEntity.frameName
					.replace(".png", "")
					.toLowerCase(),
				decorIndex: floorNumber(Math.random() * 2) + 1,
				limit: 1,
			},
			retail_price: packPriceWithDiscount,
			currency: "EUR",
			expired_at: endDate.getTime() / 1_000,
			num_of_purchases: 0,
		};

		setImmutableStateProperty(state, "specialBuildingPack", pack);

		setTimeout((): void => {
			useSocketService().emitSaveRequest({
				stores: [EmitStores.OFFER],
			});
		}, 300);
	};

	const setLastBuyedSpecialBuildingFrameName = (
		frameName: FrameName,
	): void => {
		setImmutableStateProperty(
			state,
			"lastBuyedSpecialBuildingFrameName",
			frameName,
		);

		useSocketService().emitSaveRequest({
			stores: [EmitStores.OFFER],
		});
	};

	const setEventAdWatched = (): void => {
		const now = new Date();
		const eventAdLimit = state.value.eventAdLimit;

		let newCurrent = eventAdLimit.current + 1;
		if (newCurrent > eventAdLimit.max) {
			newCurrent = eventAdLimit.max;
		}

		setImmutableStateProperty(state, "eventAdLimit", {
			current: newCurrent,
			max: eventAdLimit.max,
			lastWatchedTimestamp: now,
		});
	};

	const resetEventAdWatched = (): void => {
		const eventAdLimit = state.value.eventAdLimit;

		setImmutableStateProperty(state, "eventAdLimit", {
			current: 0,
			max: eventAdLimit.max,
			lastWatchedTimestamp: null,
		});
	};

	// Clears only event-related offer state -> regular ad cooldowns are untouched
	const resetEventState = (): void => {
		resetEventAdWatched();

		// Only reset eventium offer cooldown, regular ad offer cooldowns stay intact
		setImmutableStateProperty(state, "rewardCooldowns", {
			...state.value.rewardCooldowns,
			[RewardType.EVENTIUM]: 0,
		});

		// Drop a stale eventium ad offer if the event was restarted mid-session
		if (state.value.currentOffer?.type === RewardType.EVENTIUM) {
			setImmutableStateProperty(state, "currentOffer", null);
			setImmutableStateProperty(state, "isVisible", false);
			setImmutableStateProperty(state, "isActive", false);
		}
	};

	const clearSpecialBuildingPack = (): void => {
		setImmutableStateProperty(state, "specialBuildingPack", null);

		setTimeout((): void => {
			useSocketService().emitSaveRequest({
				stores: [EmitStores.OFFER],
			});
		}, 300);
	};

	const canShowOffer = (force?: boolean): boolean => {
		if (force) return true;

		const offerConfig = getActiveConfig();
		const now = Date.now();
		const timeSinceStart = now - gameStartTime.value;
		const timeSinceLastOffer = now - state.value.lastOfferTime;

		if (useQuestStore().tutorialActive || useUserStore().playerLevel < 3) {
			return false;
		}

		if (state.value.isActive || currentOffer.value) {
			return false;
		}

		if (timeSinceStart < offerConfig.timing.minGameTimeForOffer) {
			return false;
		}

		if (
			state.value.lastOfferTime > 0 &&
			timeSinceLastOffer < offerConfig.timing.offerCooldown
		) {
			return false;
		}

		const availableRewards = getAvailableRewardTypes();
		return availableRewards.length > 0;
	};

	const getAvailableRewardTypes = (): RewardType[] => {
		const now = Date.now();

		let availableRewardTypes = Object.values(RewardType);

		if (useEventStore().isActive) {
			if (
				eventAdLimit.value.lastWatchedTimestamp &&
				new Date(
					eventAdLimit.value.lastWatchedTimestamp,
				).toDateString() !== new Date().toDateString()
			) {
				resetEventAdWatched();
			}
		}

		// To highen the chance of getting Eventium during active event
		availableRewardTypes.push(RewardType.EVENTIUM);
		availableRewardTypes.push(RewardType.EVENTIUM);

		const activeEvent =
			useEventStore().isActive &&
			useEventStore().attributes?.level_unlock;

		if (
			!useEventStore().isActive ||
			(activeEvent &&
				(eventAdLimit.value.current >= eventAdLimit.value.max ||
					(useEventStore().attributes.level_unlock ?? 15) >
						useUserStore().playerLevel))
		) {
			availableRewardTypes = availableRewardTypes.filter(rewardType => {
				return !rewardType.toLowerCase().includes(RewardType.EVENTIUM);
			});
		}

		if (
			useRuntimeStore().superClickerType !== SuperClickerEnum.TEN &&
			useRuntimeStore().superClickerActive
		) {
			const ALL_CLICKER_TYPES = "clicker";
			availableRewardTypes = availableRewardTypes.filter(rewardType => {
				return !rewardType.includes(ALL_CLICKER_TYPES);
			});
		}

		return availableRewardTypes.filter(rewardType => {
			const cooldownEnd = state.value.rewardCooldowns[rewardType];
			return now >= cooldownEnd;
		});
	};

	const calculateCoinsReward = (): number => {
		const offerConfig = getActiveConfig();
		const resourcesStore = useResourcesStore();
		const basePassiveIncome = ceilNumber(resourcesStore.coinsIncome ?? 0);
		const passiveIncome = basePassiveIncome > 0 ? basePassiveIncome : 1;
		const config = offerConfig.rewards.coins;
		const coins = ceilNumber(
			config.baseAmount + passiveIncome * config.passiveIncomeMultiplier,
		);

		return config.roundToThousand
			? ceilNumber(coins / 1_000) * 1_000
			: coins;
	};

	const calculateEventiumReward = (): number => {
		const resourcesStore = useResourcesStore();
		const passiveIncome = resourcesStore.eventiumIncome || 0;

		let addConstant = 0;
		if (
			[EventCategory.EASTER, EventCategory.FIREFEST].includes(
				useEventStore().attributes.type!,
			)
		) {
			addConstant = 100;
		}
		if (
			[EventCategory.HALLOWEEN, EventCategory.PIRATES].includes(
				useEventStore().attributes.type!,
			)
		) {
			addConstant = 500;
		}

		if (
			[
				EventCategory.FISHING,
				EventCategory.SOLAR,
				EventCategory.HUNTING,
			].includes(useEventStore().attributes.type!)
		) {
			addConstant = 1000;
		}

		return ceilNumber(passiveIncome * 6 * 10 + addConstant); // 10 nasobok minutoveho incomu + konstantny bonus podla eventu
	};

	const generateRandomReward = (): RewardOffer => {
		const offerConfig = getActiveConfig();
		const availableTypes = getAvailableRewardTypes();

		if (useUserStore().adRewardsCount === 0) {
			return {
				type: RewardType.GEMS,
				amount: offerConfig.rewards.gems.amount,
				icon: offerConfig.display.icons.gems,
				description: offerConfig.display.descriptions.gems,
			};
		}

		if (availableTypes.length === 0) {
			return {
				type: RewardType.COINS,
				amount: calculateCoinsReward(),
				icon: offerConfig.display.icons.coins,
				description: offerConfig.display.descriptions.coins,
			};
		}

		const randomType =
			availableTypes[floorNumber(Math.random() * availableTypes.length)];

		switch (randomType) {
			case RewardType.COINS:
				return {
					type: RewardType.COINS,
					amount: calculateCoinsReward(),
					icon: offerConfig.display.icons.coins,
					description: offerConfig.display.descriptions.coins,
				};

			case RewardType.GEMS:
				return {
					type: RewardType.GEMS,
					amount: offerConfig.rewards.gems.amount,
					icon: offerConfig.display.icons.gems,
					description: offerConfig.display.descriptions.gems,
				};

			case RewardType.CLICKER_2X:
				return {
					type: RewardType.CLICKER_2X,
					amount: offerConfig.rewards.clicker2x.amount,
					icon: offerConfig.display.icons.clicker2x,
					description: offerConfig.display.descriptions.clicker2x,
				};

			case RewardType.CLICKER_3X:
				return {
					type: RewardType.CLICKER_3X,
					amount: offerConfig.rewards.clicker3x.amount,
					icon: offerConfig.display.icons.clicker3x,
					description: offerConfig.display.descriptions.clicker3x,
				};

			case RewardType.CLICKER_5X:
				return {
					type: RewardType.CLICKER_5X,
					amount: offerConfig.rewards.clicker5x.amount,
					icon: offerConfig.display.icons.clicker5x,
					description: offerConfig.display.descriptions.clicker5x,
				};

			case RewardType.CHESTS:
				return {
					type: RewardType.CHESTS,
					amount: offerConfig.rewards.chests.amount,
					icon: offerConfig.display.icons.chests,
					description: offerConfig.display.descriptions.chests,
				};

			case RewardType.SUPER_CLICKERS:
				return {
					type: RewardType.SUPER_CLICKERS,
					amount: offerConfig.rewards.superClickers.amount,
					icon: offerConfig.display.icons.superClickers,
					description: offerConfig.display.descriptions.superClickers,
				};

			case RewardType.EVENTIUM:
				return {
					type: RewardType.EVENTIUM,
					amount: calculateEventiumReward(),
					icon: `${useEventStore().attributes.type}-${offerConfig.display.icons.eventium}`,
					description: offerConfig.display.descriptions.eventium,
				};

			default:
				return {
					type: RewardType.COINS,
					amount: calculateCoinsReward(),
					icon: offerConfig.display.icons.coins,
					description: offerConfig.display.descriptions.coins,
				};
		}
	};

	const setVisibility = (visible: boolean): void => {
		setImmutableStateProperty(state, "isVisible", visible);

		const popupStore = usePopupStore();

		if (visible) {
			popupStore.showPopup({
				id: PopupDictionary.LB_REWARDED_AD,
				type: PopupType.REWARDED_AD,
				heading: translate("ui.rewarded_ad"),
				maxHeight: 26,
				width: 24,
				percentageWidth: "95%",
				skin: SkinTypes.LIGHT,
				largeHeader: true,
				closeable: false,
				isPaddingless: true,
				component: markRaw(RewardedPopup),
			});
		} else {
			// The popup is hidden to ensure consistency between store state and UI.
			popupStore.hidePopup();
		}
	};

	const setActive = (active: boolean): void => {
		setImmutableStateProperty(state, "isActive", active);
	};

	const generateOffer = (): void => {
		const offer = generateRandomReward();
		setImmutableStateProperty(state, "currentOffer", offer);
		// Update lastOfferTime when generating offer to prevent immediate regeneration
		setImmutableStateProperty(state, "lastOfferTime", Date.now());
		useUserStore().setAdRewardsCount(useUserStore().adRewardsCount + 1);

		// If an offer is available
		// and if the first ad reward is processed, a tutorial hint is displayed
		if (
			offer &&
			useUserStore().adRewardsCount === 1 &&
			!__APP_IS_OLIVER__
		) {
			usePopupStore().showPopup({
				type: PopupType.INFO,
				heading: translate("ui.rewarded_ad"),
				body: translate("tutorial.rewarded_ad"),
				skin: SkinTypes.LIGHT,
				isCentered: true,
				maxHeight: 25,
				maxZIndex: true,
				width: 22,
				closeCallback: (): void => {
					useQuestStore().setIsTutorialHighlightingUI(true);
				},
			});
		}
	};

	const acceptOffer = (): void => {
		const offer = currentOffer.value as unknown as RewardOffer;
		if (!offer) return;

		// Activate the offer to prevent clearing during rewards
		setActive(true);

		// Set cooldowns
		setCooldowns();
	};

	const rejectOffer = (): void => {
		// Set cooldowns even on rejection
		setCooldowns();

		// Deactivate and hide offer
		resetOffer();
	};

	const setCooldowns = (): void => {
		const offerConfig = getActiveConfig();
		const now = Date.now();
		const offer = currentOffer.value as unknown as RewardOffer;

		setImmutableStateProperty(state, "lastOfferTime", now);

		if (offer) {
			const cooldownDuration =
				offerConfig.timing.rewardCooldowns[offer.type];
			setImmutableStateProperty(state, "rewardCooldowns", {
				...state.value.rewardCooldowns,
				[offer.type]: now + cooldownDuration,
			});
		}
	};

	const applyReward = (): boolean => {
		const offer = currentOffer.value as unknown as RewardOffer;
		// Only apply rewards for active offers (those that have been accepted)
		if (!offer || !state.value.isActive) return false;

		const resourcesStore = useResourcesStore();

		switch (offer.type) {
			case RewardType.COINS:
				resourcesStore.increase(
					ResourceType.COINS,
					offer.amount,
					"Offer reward: coins",
				);
				break;

			case RewardType.GEMS:
				resourcesStore.increase(
					ResourceType.GEMS,
					offer.amount,
					"Offer reward: gems",
				);
				break;

			case RewardType.CLICKER_2X:
				resourcesStore.increase(
					ResourceType.CLICKER_2,
					offer.amount,
					"Offer reward: 2x clicker",
				);
				break;

			case RewardType.CLICKER_3X:
				resourcesStore.increase(
					ResourceType.CLICKER_3,
					offer.amount,
					"Offer reward: 3x clicker",
				);
				break;

			case RewardType.CLICKER_5X:
				resourcesStore.increase(
					ResourceType.CLICKER_5,
					offer.amount,
					"Offer reward: 5x clicker",
				);
				break;

			case RewardType.CHESTS:
				// Generate chests on map
				generateChests(offer.amount);
				break;

			case RewardType.SUPER_CLICKERS:
				resourcesStore.increase(
					ResourceType.SUPER_CLICKER,
					offer.amount,
					"Offer reward: super clickers",
				);
				break;

			case RewardType.EVENTIUM:
				setEventAdWatched();
				resourcesStore.increase(
					ResourceType.EVENTIUM,
					offer.amount,
					"Offer reward: super clickers",
				);
				break;
		}

		// Deactivate the offer and clear it completely
		resetOffer();

		return true;
	};

	const generateChests = (count: number): void => {
		try {
			const chestrepo = ChestRepository.getInstance();
			if (!chestrepo.initialized) {
				chestrepo.initialize(useTileRepository());
			}
			for (let i = 0; i < count; i++) {
				// Try to generate a normal chest, fallback if fails
				const success = chestrepo.tryGenerateChest(
					ChestType.REWARDED_AD,
				);
				if (!success) {
					console.error(`Failed to generate chest ${i + 1}/${count}`);
				}
			}
		} catch (error) {
			console.error("Error generating chests:", error);
		}
	};

	const checkAndShowOffer = (force: boolean = false): void => {
		// IMPORTANT: Skip generating during tutorial
		if (useQuestStore().tutorialActive) return;
		if (useUserStore().playerLevel < 3) return;

		// Don't show offer if one is already visible or active
		if (isVisible.value || currentOffer.value || state.value.isActive) {
			const activeEvent =
				useEventStore().isActive &&
				useEventStore().attributes?.level_unlock;

			if (
				(currentOffer.value as unknown as RewardOffer).type ===
					RewardType.EVENTIUM &&
				!activeEvent
			) {
				// Regeneration triggered as the event-based offer is still present, but the event itself is no longer active
				generateOffer();
			}
			return;
		}

		if (canShowOffer(force)) {
			generateOffer();
		}
	};

	const resetOffer = (): void => {
		// Only reset if there's still an active or visible offer
		// If applyReward() was already called, the offer would have been cleared
		if (state.value.isActive || currentOffer.value) {
			setActive(false);
			setVisibility(false);
			setImmutableStateProperty(state, "currentOffer", null);
		}
	};

	return {
		$getPayload,
		$resetPayload,
		$setState,
		isVisible,
		setVisibility,
		currentOffer,
		gameStartTime,
		specialBuildingPack,
		lastBuyedSpecialBuildingFrameName,
		eventAdLimit,
		canShowOffer,
		generateOffer,
		acceptOffer,
		rejectOffer,
		checkAndShowOffer,
		applyReward,
		setActive,
		resetOffer,
		setSpecialBuildingPack,
		clearSpecialBuildingPack,
		setLastBuyedSpecialBuildingFrameName,
		setEventAdWatched,
		resetEventAdWatched,
		resetEventState,
	};
});
